import { pipe, Record, Option } from "fp";

import { Reference, ID } from "~/Data";
import { Request as GenericRequest } from "~/Request";
import { Time } from "~/Time";

export type Cache<A> = {
  readonly lastUpdate: Time.DateTime;
  readonly data: Data<A>;
};

export type Data<A> = Record<string, A>;
export type Request<A> = GenericRequest.Fn<Cache<A>>;

export const empty = <A>(
  lastUpdate: Time.DateTime = Time.DateTime.now()
): Cache<A> => ({ lastUpdate, data: {} });

export const equals = <A>(a: Cache<A>) => (b: Cache<A>): boolean =>
  a.lastUpdate !== b.lastUpdate;

export const isEmpty = <A>({ data }: Cache<A>): boolean => Record.isEmpty(data);

export const fromData = <A>(data: Data<A>): Cache<A> => ({
  ...empty(),
  data,
});

export const fromList = <A, B>(mapWithID: (a: A) => readonly [ID.ID, B]) => (
  list: readonly A[]
): Cache<B> => {
  const cache: Cache<B> = empty();

  // eslint-disable-next-line functional/no-loop-statement
  for (const a of list) {
    const [id, b] = mapWithID(a);
    // eslint-disable-next-line functional/no-expression-statement,functional/immutable-data
    cache.data[id] = b;
  }

  return cache;
};

export const fromIndividual = <A, B>(mapWithID: (a: A) => readonly [ID.ID, B]) => (
  driver: A
): Cache<B> => {
  const cache: Cache<B> = empty();

  const [id, b] = mapWithID(driver);
  // eslint-disable-next-line functional/immutable-data
  cache.data[id] = b

  return cache;
};

export const updated = <A>(cache: Cache<A>): Cache<A> => ({
  ...cache,
  lastUpdate: Time.DateTime.now(),
});

export const concat = <A>(a: Cache<A>) => (b: Cache<A>): Cache<A> => ({
  lastUpdate: a.lastUpdate > b.lastUpdate ? a.lastUpdate : b.lastUpdate,
  data: { ...a.data, ...b.data },
});

export const map = <A, B>(map: (id: ID.ID, a: A) => B) => (
  cache: Cache<A>
): Cache<B> => ({
  ...cache,
  data: pipe(cache.data, Record.mapWithIndex(map)),
});

export const reduce = <A, B>(b: B, reduce: (id: ID.ID, b: B, a: A) => B) => (
  cache: Cache<A>
): B => Record.reduceWithIndex(b, reduce)(cache.data);

export const toList = <A, B>(map: (id: ID.ID, a: A) => B) => ({
  data,
}: Cache<A>): readonly B[] => pipe(data, Record.collect(map));

export namespace Filter {
  export type Filter<A, B> = (id: ID.ID, a: A) => false | B;
  export const by = <A, B>(filter: Filter<A, B>) => (
    cache: Cache<A>
  ): Cache<B> =>
    pipe(
      cache.data,
      Record.filterMapWithIndex((id, a) =>
        pipe(filter(id, a), (filtered) =>
          filtered ? Option.some(filtered) : Option.none
        )
      ),

      (filteredData) => ({ ...cache, data: filteredData })
    );
}

export namespace Get {
  export type Get<A, B> = Partial<Reference.Reference> & {
    readonly missing: () => B;
    readonly found: (a: A) => B;
  };

  export const fromReference = <A, B>({ id, missing, found }: Get<A, B>) => (
    cache: Cache<A>
  ): B =>
    pipe(
      Option.fromNullable(id),
      Option.mapNullable((id) => cache.data[id]),
      Option.map(found),
      Option.getOrElse(missing)
    );

  export const fromFilter = <A, B>(filter: Filter.Filter<A, B>) => (
    cache: Cache<A>
  ): B | undefined =>
    pipe(
      cache,
      Filter.by(filter),
      toList((_id, b) => b),
      ([first]) => first
    );
}

export namespace Update {
  export type Update<A> = Partial<Get.Get<A, A | undefined>>;

  export const removeFromReference = <A>(
    reference: Partial<Reference.Reference>
  ) => (cache: Cache<A>): Cache<A> =>
    pipe(
      cache,
      fromReference({
        ...reference,
        found: (): A | undefined => undefined,
      })
    );

  export const fromReference = <A>({
    id,
    missing = () => undefined,
    found = (a) => a,
  }: Partial<Update<A>>) => (cache: Cache<A>): Cache<A> =>
    pipe(
      Option.fromNullable(id),
      Option.fold(
        () => cache,
        (id) =>
          pipe(
            cache,
            Get.fromReference({ id, found, missing }),
            Option.fromNullable,
            Option.map((updated) => ({ ...cache.data, [id]: updated })),
            Option.getOrElse(() => {
              const data = { ...cache.data };

              // eslint-disable-next-line functional/no-expression-statement,functional/immutable-data
              delete data[id];
              return data;
            }),

            (data) => updated({ ...cache, data })
          )
      )
    );
}
